import { App, Button, Flex, Input, theme, Typography } from 'antd';
import { type SortOrder } from 'antd/es/table/interface';
import { CircleAlert, Copy, PlusCircle, Trash2 } from 'lucide-react';
import React, { type Key, type ReactNode, useEffect, useReducer, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import MealsFilter, { type MealsFiltersProps } from './meals.filter';
import AuthorizedComponent from '../../../components/authorized-component';
import { DataTable, type DataTableProps } from '../../../components/data-table';
import SyncSuspense from '../../../components/sync-suspense';
import { ExtraContent } from '../../../contexts/extra-content-context-provider';
import { MealCategoriesProvider } from '../../../contexts/meal-categories-provider';
import { MealsProvider } from '../../../contexts/meals-context-provider';
import {
  Meal,
  MealCategory,
  MealItem,
  MealItemAssignment,
  MealItemAssignmentIngredient,
  Measure,
  ServingTool,
} from '../../../entities';
import useDebouncedState from '../../../hooks/use-debounced-state';
import { useNotification } from '../../../hooks/use-notification';
import { AtomicOperation, useAtomicOperations, useQuery } from '../../../jsonapi-provider';
import { Roles } from '../../../types';
import { randomUUID } from '../../../utils/utils';

const MealFormLazy = React.lazy(async () => await import('./meal.form'));

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_SORT_FIELD = 'name';
const DEFAULT_SORT_DIRECTION: SortOrder = 'ascend';
const SUCCESS_CLONE_MESSAGE = 'Meal cloned successfully';
const SUCCESS_DELETE_MESSAGE = 'Meal deleted successfully';

interface MealsPageState {
  filters?: MealsFiltersProps['initialFilters'];
  pagination: { current: number; pageSize: number };
  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

const serializeSort = (sort: MealsPageState['sort']): string | undefined => {
  if (sort.field && sort.order) {
    const field = Array.isArray(sort.field) ? sort.field.join(',') : sort.field.toString();
    return `${sort.order === 'descend' ? '-' : ''}${field}`;
  }
  return undefined;
};

const serializeFilters = (filters: MealsPageState['filters']): string | undefined => {
  const parts: string[] = [];
  if (filters?.query) parts.push(`name=ini='*${filters.query}*'`);

  return parts.length ? parts.join(';') : undefined;
};

const reducer = (
  state: MealsPageState,
  action: {
    type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT';
    payload?: MealsPageState['filters'] | MealsPageState['pagination'] | MealsPageState['sort'];
  }
): MealsPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: {} };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload as MealsPageState['filters'],
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload as MealsPageState['pagination'],
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as MealsPageState['sort'],
      };
  }
};

const MealsPage = (): ReactNode => {
  const { modal } = App.useApp();
  const notification = useNotification();
  const { token } = theme.useToken();
  const [searchParams, setSearchParams] = useSearchParams();
  const mealId = searchParams.get('id');
  const [state, setState] = useReducer(reducer, {
    filters: {},
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const [debouncedState] = useDebouncedState({ state, time: 1000 });
  const [mealToEdit, setMealToEdit] = useState<Partial<Meal>>();
  const meals = useQuery<Meal[]>(
    debouncedState && [
      'meal',
      {
        filter: serializeFilters(debouncedState.filters),
        page: {
          size: debouncedState.pagination.pageSize,
          number: debouncedState.pagination.current,
          totals: 1,
        },
        sort: serializeSort(debouncedState.sort),
        fields: { meal: ['name', 'season', 'weekNumber'] },
      },
    ]
  );
  const meal = useQuery<Meal>(mealId && ['meal', mealId]);

  useEffect(() => {
    if (meal.data) {
      setMealToEdit(meal.data);
    }
  }, [meal.data]);
  useEffect(() => {
    meal.error &&
      notification.error({
        message: ' ',
        description: meal.error.detail,
      });
  }, [meal.error]);
  useEffect(() => {
    meals.error &&
      notification.error({
        message: ' ',
        description: meals.error.detail,
      });
  }, [meals.error]);
  const columns: DataTableProps<Meal>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            searchParams.set('id', item?.id ?? '');
            setSearchParams(searchParams);
            setMealToEdit(item);
          }}
        >
          {value}
        </Typography.Link>
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
    {
      title: 'Season',
      dataIndex: 'season',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
    {
      title: 'Week Number',
      dataIndex: 'weekNumber',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
  ];
  const markMealAsSpecialMeal = (): void => {
    setMealToEdit((prev) => Object.assign(new Meal(), prev, { isSpecialEventMeal: true }));
  };
  const atomicOperations = useAtomicOperations();

  const cloneMeal = (mealToClone: Meal, newMealName: string): void => {
    const mealToBeSaved = new Meal(
      undefined,
      newMealName,
      mealToClone.season,
      mealToClone.weekNumber,
      [],
      null
    );

    from(
      meals.client.fetch([
        'meal',
        mealToClone.id,
        'mealItemAssignments',
        {
          include: [
            'mealCategory',
            'baseMealItem',
            'measure',
            'servingMeasure',
            'servingTool',
            'mealItemAssignmentIngredients.ingredient',
            'mealItemAssignmentIngredients.measure',
          ],
          page: { limit: 10000 },
        },
      ])
    )
      .pipe(
        switchMap((res) => {
          // @ts-expect-error operand of delete operator must be optional
          delete mealToBeSaved.mealItemAssignments;
          // @ts-expect-error operand of delete operator must be optional
          delete mealToBeSaved.isSpecialEventMeal;
          // @ts-expect-error operand of delete operator must be optional
          delete mealToBeSaved.specialEventMeal;

          const mealOperation = AtomicOperation.add(mealToBeSaved, randomUUID());

          const operations: Array<
            AtomicOperation<Meal | MealItemAssignment | MealItemAssignmentIngredient>
          > = [mealOperation];

          if (res.data?.length > 0) {
            const lids = res.data!.map(() => randomUUID());
            operations.push(
              ...AtomicOperation.addNestedResources<Meal, MealItemAssignment>(
                mealToBeSaved,
                mealOperation.lid,
                res.data!.map(
                  (mia: MealItemAssignment) =>
                    new MealItemAssignment(
                      undefined,
                      new MealCategory(mia.mealCategory.id),
                      new MealItem(mia.baseMealItem.id),
                      mia.serveType,
                      mia.sortOrder,
                      mia.servingSize,
                      mia.servingMeasure ? new Measure(mia.servingMeasure.id) : undefined,
                      mia.size,
                      mia.measure ? new Measure(mia.measure.id) : undefined,
                      mia.servingTool ? new ServingTool(mia.servingTool.id) : undefined,
                      []
                    )
                ),
                lids
              )
            );

            operations.push(
              ...(res.data! as MealItemAssignment[])
                .map((mia, idx) =>
                  mia.mealItemAssignmentIngredients.map((miai) =>
                    AtomicOperation.add(
                      new MealItemAssignmentIngredient(
                        undefined,
                        miai.ingredient,
                        miai.units,
                        miai.measure,
                        new MealItemAssignment(lids[idx]),
                        miai.originalUnits,
                        miai.sortOrder
                      ),
                      undefined,
                      [
                        'meal',
                        mealOperation.lid,
                        'mealItemAssignments',
                        lids[idx],
                        'mealItemAssignmentIngredients',
                      ]
                    )
                  )
                )
                .flat()
            );
          }

          return atomicOperations.operate(operations).pipe(
            switchMap(([me]) => of<Meal>(Object.assign(new Meal(), me))),
            tap(() => {
              notification.success({
                message: ' ',
                description: SUCCESS_CLONE_MESSAGE,
              });
            }),
            tap(() => {
              meals.refetch?.();
            }),
            catchError((error: Error) => {
              notification.error({
                message: ' ',
                description: error.message,
              });
              return EMPTY;
            })
          );
        })
      )
      .subscribe();
  };

  return (
    <Flex gap="middle" vertical>
      <DataTable<Meal>
        title="Meals"
        description="List of meals to be served to customers"
        filtersNode={
          <MealsFilter
            initialFilters={state.filters}
            onChange={(filters) => {
              setState({ type: 'SET_FILTER', payload: filters });
            }}
          />
        }
        actionsNode={
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <Button
              type="primary"
              icon={<PlusCircle />}
              onClick={() => {
                setMealToEdit({});
              }}
            >
              Add Meal
            </Button>
          </AuthorizedComponent>
        }
        currentPage={state.pagination.current}
        dataList={meals}
        onChange={setState}
        columns={columns}
        actions={[
          {
            key: 'delete',
            label: 'Delete',
            icon: <Trash2 />,
            danger: true,
            onItemClick: (record) => {
              void modal.confirm({
                width: '50%',
                title: 'Confirm deletion',
                icon: <CircleAlert />,
                content: (
                  <Typography.Text>
                    Are you sure you want to delete <b>{record.name}</b> permanently?
                  </Typography.Text>
                ),
                okText: 'Yes',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => {
                  from(meals.client.delete(['meal', record.id]))
                    .pipe(
                      switchMap((res) =>
                        res.error
                          ? throwError(() => new Error(res.error?.detail ?? res.error?.title))
                          : of(res.data)
                      ),
                      tap(() => {
                        notification.success({
                          message: ' ',
                          description: SUCCESS_DELETE_MESSAGE,
                        });
                        meals.refetch?.();
                      }),
                      catchError((error: Error) => {
                        notification.error({
                          message: 'Failed to delete meal',
                          description: error.message,
                        });
                        return EMPTY;
                      })
                    )
                    .subscribe();
                },
              });
            },
          },
          {
            key: 'clone',
            label: 'Clone meal',
            icon: <Copy />,
            onItemClick: (record) => {
              let tempInputValue = `${record.name} - clone`;
              void modal.confirm({
                width: '50%',
                title: ' Confirm Clone',
                icon: <CircleAlert />,
                content: (
                  <Flex vertical gap="small">
                    <Typography.Text>
                      Clone meal <b>{record.name}</b> to
                    </Typography.Text>
                    <Input
                      style={{ width: '50%' }}
                      placeholder="Enter meal name"
                      defaultValue={tempInputValue}
                      onChange={(e) => (tempInputValue = e.target.value)}
                    />
                  </Flex>
                ),
                okType: 'primary',
                onOk: async () => {
                  cloneMeal(record, tempInputValue);
                },
              });
            },
          },
        ]}
      />
      <SyncSuspense fallback="" condition={mealToEdit}>
        <ExtraContent>
          <MealCategoriesProvider>
            <MealsProvider>
              <MealFormLazy
                key={mealToEdit?.id ?? randomUUID()}
                meal={mealToEdit ?? {}}
                onSuccess={(closeForm: boolean, meal: Meal) => {
                  meals.refetch?.();
                  if (closeForm) {
                    searchParams.delete('id');
                    setSearchParams(searchParams);
                    setMealToEdit(undefined);
                  } else {
                    setMealToEdit({ ...meal });
                  }
                }}
                onCancel={() => {
                  searchParams.delete('id');
                  setSearchParams(searchParams);
                  setMealToEdit(undefined);
                }}
                markMealAsSpecialMeal={markMealAsSpecialMeal}
              />
            </MealsProvider>
          </MealCategoriesProvider>
        </ExtraContent>
      </SyncSuspense>
    </Flex>
  );
};

export default MealsPage;
